import { motion, useScroll, useTransform } from "framer-motion";
import { business, images, heroNailDetail, PRIMARY_CTA_URL } from "../data/business";

const line = {
    hidden: { y: "110%" },
    show: (i) => ({
        y: "0%",
        transition: { duration: 1, delay: 0.35 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
    }),
};

export default function Hero() {
    const { scrollY } = useScroll();
    const imgY = useTransform(scrollY, [0, 600], [0, 50]);

    return (
        <section
            id="inicio"
            data-testid="hero-section"
            className="relative overflow-hidden bg-paper pt-10 sm:pt-14 lg:pt-20"
        >
            <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-10 px-4 sm:px-8 lg:grid-cols-12 lg:gap-14 lg:px-16">
                <div className="lg:col-span-7">
                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.2, duration: 0.8 }}
                        className="mb-6 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.3em] text-accent-deep"
                    >
                        <span className="h-1.5 w-1.5 rounded-full bg-accent" />
                        Nail Designer • Uruaçu - GO
                    </motion.p>

                    <h1 className="font-display text-[clamp(2.4rem,5vw,4.5rem)] font-medium uppercase leading-[1.06] tracking-tight text-ink">
                        {["Beleza que chama", "atenção.", "Técnica que faz", "a diferença."].map(
                            (l, i) => (
                                <span key={l} className="block overflow-hidden pb-1">
                                    <motion.span
                                        className={i % 2 === 1 ? "block italic text-accent" : "block"}
                                        variants={line}
                                        custom={i}
                                        initial="hidden"
                                        animate="show"
                                    >
                                        {l}
                                    </motion.span>
                                </span>
                            ),
                        )}
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.9, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                        className="mt-7 max-w-md text-sm leading-relaxed text-ink-soft sm:text-base"
                    >
                        Alongamentos e cuidados realizados com técnica, atenção aos
                        detalhes e acabamento pensado para valorizar o seu estilo.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.05, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                        className="mt-9 flex flex-col items-start gap-4 sm:flex-row sm:items-center"
                    >
                        <a
                            href={PRIMARY_CTA_URL}
                            data-testid="hero-cta-primary"
                            className="group inline-flex items-center gap-3 bg-ink px-7 py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-paper transition-colors duration-300 hover:bg-accent"
                        >
                            Agendar meu horário
                            <span className="inline-block transition-transform duration-300 group-hover:translate-x-1">
                                →
                            </span>
                        </a>
                        <a
                            href="#trabalhos"
                            data-testid="hero-cta-secondary"
                            className="inline-flex items-center gap-3 border border-ink/20 px-7 py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:border-ink hover:bg-ink hover:text-paper"
                        >
                            Ver resultados
                        </a>
                    </motion.div>

                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 1.3, duration: 0.8 }}
                        className="mt-8 font-mono text-[10px] uppercase tracking-[0.25em] text-ink-soft/70"
                    >
                        {business.name} — Uruaçu - GO
                    </motion.p>
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                    className="relative lg:col-span-5"
                >
                    <div className="absolute -right-3 -top-3 h-full w-full border border-accent/40 sm:-right-5 sm:-top-5" />
                    <motion.div
                        style={{ y: imgY }}
                        className="relative overflow-hidden bg-cream"
                    >
                        <img
                            src={images.portrait}
                            alt="Danielly Sousa, nail designer do Studio Danielly Sousa"
                            className="aspect-[4/5] w-full object-cover object-top"
                            loading="eager"
                        />
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 24, rotate: -4 }}
                        animate={{ opacity: 1, y: 0, rotate: -3 }}
                        transition={{ delay: 1.2, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                        className="absolute -bottom-8 -left-4 w-32 border-4 border-paper bg-paper shadow-xl sm:-left-10 sm:w-40"
                    >
                        <img
                            src={heroNailDetail}
                            alt="Detalhe de um trabalho real em unhas"
                            className="aspect-square w-full object-cover"
                        />
                        <p className="py-2 text-center font-mono text-[8px] uppercase tracking-[0.2em] text-ink-soft">
                            Trabalho real
                        </p>
                    </motion.div>
                </motion.div>
            </div>

            <div className="h-16 sm:h-20" />
        </section>
    );
}
